/** QuickHouse — Vue 3D navigable (react-three-fiber + drei) */
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import { useProjectStore } from '../store/useProjectStore.js';
import { floorElevations } from '../../shared/plan3d.js';
import FloorMesh from './FloorMesh.jsx';
import ClipPlanes from './ClipPlanes.jsx';
import WalkControls from './WalkControls.jsx';
import WalkHud from './WalkHud.jsx';
import Measure3D from './Measure3D.jsx';

export default function Viewer3D() {
  const project = useProjectStore((s) => s.project);
  const hiddenFloors = useProjectStore((s) => s.hiddenFloors);
  const explode = useProjectStore((s) => s.explode);
  const walkMode = useProjectStore((s) => s.walkMode);
  const measure3d = useProjectStore((s) => s.measure3d);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  if (!project) return null;

  const slab = project.settings.slabThickness;
  const elevations = new Map(
    floorElevations(project.floors, slab).map((e) => [e.floorId, e]),
  );
  const walkElevation = (elevations.get(activeFloorId)?.elevation ?? 0) / 100;

  // Mesure 3D : chaque clic (non glissé) ajoute le point d'impact sur la surface visée.
  // En mode visite, c'est WalkControls qui mesure au viseur central (pointeur capturé).
  const onMeasureClick = (e) => {
    if (!measure3d || walkMode) return;
    e.stopPropagation();
    useProjectStore.getState().addMeasurePoint({ x: e.point.x, y: e.point.y, z: e.point.z });
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
    <Canvas shadows camera={{ position: [10, 9, 10], fov: 50 }}>
      <color attach="background" args={['#0c0e14']} />
      <ClipPlanes />
      <hemisphereLight args={['#ffffff', '#44506a', 0.75]} />
      <directionalLight
        position={[12, 18, 6]}
        intensity={1.4}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-25}
        shadow-camera-right={25}
        shadow-camera-top={25}
        shadow-camera-bottom={-25}
        shadow-bias={-0.0001}
        shadow-normalBias={0.05}
      />
      <Grid
        position={[0, -0.02, 0]}
        args={[80, 80]}
        cellSize={0.5}
        cellColor="#1d2435"
        sectionSize={5}
        sectionColor="#2a3450"
        fadeDistance={70}
        infiniteGrid
      />
      <group onClick={onMeasureClick}>
      {project.floors.map((floor) => {
        if (hiddenFloors[floor.id]) return null;
        const info = elevations.get(floor.id);
        const elevation = info.elevation + explode * 100 * Math.max(0, info.level);
        // Trémies : escaliers de l'étage juste en dessous
        const below = project.floors.find((f) => f.level === floor.level - 1);
        const stairsBelow = (below?.stairs ?? []).filter((s) => !s.hidden && s.cutSlab !== false);
        // Étages supérieurs : chaque mur comble la bande de dalle sous lui (façade continue)
        const minLevel = Math.min(...project.floors.map((f) => f.level));
        return (
          <FloorMesh
            key={floor.id}
            floor={floor}
            elevation={elevation}
            slabThickness={slab}
            stairsBelow={stairsBelow}
            belowFloor={below ?? null}
            bottomFill={floor.level > minLevel ? slab : 0}
          />
        );
      })}
      </group>
      <Measure3D />
      {walkMode
        ? <WalkControls startElevation={walkElevation} />
        : <OrbitControls makeDefault target={[3, 1, 3]} />}
    </Canvas>
    <WalkHud />
    </div>
  );
}
